import React, { useEffect, useState } from "react";

import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Error from "Components/Error";
import Spinner from "Components/Spinner";
import { ACTIVITY_TYPES, SUBSCRIPTION_DEFAULTS } from "Constants/constants";
import * as Sticker from "ds/Sticker";
import useInterval from "Hooks/useInterval";
import useTimeout from "Hooks/useTimeout";
import logger from "Libs/logger";
import client from "Libs/platform";
import { getGitRepoName, getOwnerInfoName } from "Libs/utils";
import { getAccountInfo } from "Reducers/app";
import {
  selectFeatureOrganizationsEnabled,
  selectFeatureUnbrandedArtEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { organizationsSelector } from "Reducers/organization/organization";
import { openProjectWizard } from "Reducers/project/wizard";
import {
  setupOptionsConfigSelector,
  setupOptionsSelector
} from "Reducers/setupOptions/setupOptions.selectors";
import { addSubscription } from "Reducers/subscription";
import { useAppSelector } from "Store/hooks";

import * as S from "./Building.styles";

const Building = ({ goToStep }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { state } = useLocation();
  const navigate = useNavigate();

  const useUnbrandedIllustrations = useSelector(
    selectFeatureUnbrandedArtEnabled
  );

  const [isPolling, setIsPolling] = useState(false);
  const [provisioningError, setProvisioningError] = useState(false);

  const organizations = useSelector(organizationsSelector);
  const config = useAppSelector(setupOptionsConfigSelector);
  const { data: options } = useAppSelector(setupOptionsSelector);
  const status = useSelector(({ subscription }) => subscription?.status);
  const errors = useSelector(({ subscription }) => subscription?.errors);
  const lastAdded = useSelector(({ subscription }) => subscription?.lastAdded);
  const organizationEnabled = useAppSelector(selectFeatureOrganizationsEnabled);

  useEffect(() => {
    if (config) {
      dispatch(addSubscription({ config: getConfig() }));
    } else {
      goToStep("/");
    }
  }, []);

  useEffect(() => {
    if (status === "added") {
      setIsPolling(true);
    }
  }, [status]);

  useInterval(
    () => {
      getSubscription();
    },
    // Delay in milliseconds or null to stop it
    isPolling ? 10000 : null
  );

  useTimeout(
    () => {
      setIsPolling(false);
    },
    900000 // 15mins
  );

  // HOTFIX: currently the api doesn't return sort plan.
  // For this reason we search if we find a development plan
  // if we can't find, we take the first in the list or the default
  const getPlan = () => {
    const plans = options?.plans;
    return (
      plans?.find(p => p.includes(SUBSCRIPTION_DEFAULTS.plan)) ||
      plans?.[0] ||
      SUBSCRIPTION_DEFAULTS.plan
    );
  };

  const getConfig = () => {
    const { defaultBranch, region, template, title, organization } = config;

    let data = {
      defaultBranch,
      title,
      region: region.id,
      storage: SUBSCRIPTION_DEFAULTS.storage,
      environments: SUBSCRIPTION_DEFAULTS.environments,
      plan: getPlan()
    };
    const isYaml = /.*.(yaml|yml)/gi.test(template);
    if (isYaml) {
      data.optionsUrl = template;
    } else if (template) {
      data.optionsCustom = {
        initialize: {
          repository: template,
          profile: getGitRepoName(template) || "Custom"
        }
      };
    }

    if (organizationEnabled) {
      data.organizationId = organization.value;
    }

    return data;
  };

  const getSubscription = async () => {
    try {
      const { organizationId } = getConfig();

      let subscription;
      // If we have an organization id and the organization flag is enabled
      // We need to call the organization API
      if (organizationEnabled && organizationId) {
        subscription = await client.getOrganizationSubscription(
          organizationId,
          lastAdded
        );
      } else {
        subscription = await client.getSubscription(lastAdded);
      }
      const options = subscription.project_options.initialize;

      if (subscription.status === "provisioning failure") {
        setIsPolling(false);
        setProvisioningError(true);
      }

      if (subscription.status === "active") {
        // If the project setup is complete proceed to the next
        // steps in the workflow.
        // @todo: possible remove this because we may not need
        // any project info outside the redirect.
        const redirect = async () => {
          setIsPolling(false);
          const organizationName = getOwnerInfoName({
            subscription,
            organizations
          });

          await dispatch(getAccountInfo()).unwrap();

          await dispatch(
            openProjectWizard({
              organizationId: organizationName,
              projectId: subscription.project_id
            })
          ).unwrap();

          navigate(`/${organizationName}/${subscription.project_id}`, {
            replace: true
          });
        };
        // If there are no options listed skip the initialization process.
        if (!options) {
          redirect();
          return;
        }

        const project = await client.getProject(subscription.project_id);
        const defaultEnvironment = await client.getEnvironment(
          subscription.project_id,
          project.default_branch
        );
        if (defaultEnvironment.has_code) {
          redirect();
          return;
        }
        const activities = await client.getProjectActivities(
          subscription.project_id
        );
        const hasInitializeActivity =
          typeof activities.find(
            activity => activity.type === ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE
          ) !== "undefined";
        if (hasInitializeActivity === true) {
          redirect();
          return;
        }
        client
          .initializeEnvironment(subscription.project_id, options)
          .then(() => {
            redirect();
          })
          .catch(err => {
            logger(err);
            redirect();
          });
      }
    } catch (err) {
      logger(err);
    }
  };

  if (provisioningError) {
    return (
      <S.Wrapper>
        <Sticker.Root priority="critical">
          <Sticker.Icon priority="critical" />
          <Sticker.Text>
            {intl.formatMessage({ id: "setup.building.provisioning_error" })}
          </Sticker.Text>
        </Sticker.Root>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      {!errors ? (
        <S.Loading>
          <S.Title tag="h2">
            {intl.formatMessage({ id: "setup.building.title" })}
          </S.Title>
          <p>{intl.formatMessage({ id: "setup.building.description" })}</p>
          <p className="close-link">
            <Link to={state?.locationAtCreateProject || "/"}>
              {intl.formatMessage({ id: "close" })}
            </Link>
          </p>
          <Spinner
            alt="Building your project"
            useUnbrandedIllustrations={useUnbrandedIllustrations}
          />
        </S.Loading>
      ) : (
        <>
          <S.ErrorHelpText>
            {intl.formatMessage(
              { id: "setup.building.errors" },
              {
                count: errors?.length,
                link: txt => (
                  <a
                    href={
                      config.CUSTOM_URL_SUPPORT
                        ? config.CUSTOM_URL_SUPPORT
                        : `${config.URL_ACCOUNTS}/support`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {txt}
                  </a>
                )
              }
            )}
          </S.ErrorHelpText>
          {<Error>{errors}</Error>}
        </>
      )}
    </S.Wrapper>
  );
};

Building.propTypes = {
  goToStep: PropTypes.func,
  organizationNameId: PropTypes.string
};

export default Building;
